import React from 'react';
import { Search, SlidersHorizontal } from 'lucide-react';
import type { MockVacancy } from '../../data/clientEngagementMock';

export interface DashboardHeaderProps {
  search: string;
  onSearchChange: (v: string) => void;
  vacancyFilter: string;
  onVacancyFilterChange: (id: string) => void;
  statusFilter: string;
  onStatusFilterChange: (v: string) => void;
  vacancies: MockVacancy[];
  /** Verborgen in het vereenvoudigde klantportaal. */
  portalSimple?: boolean;
  reviewMode?: boolean;
  onToggleReviewMode?: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  search,
  onSearchChange,
  vacancyFilter,
  onVacancyFilterChange,
  statusFilter,
  onStatusFilterChange,
  vacancies,
  portalSimple,
  reviewMode,
  onToggleReviewMode,
}) => (
  <header className="border-b border-slate-200 bg-white px-4 pb-5 pt-3 sm:px-6 lg:px-8">
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
            {portalSimple ? 'Samenwerking' : 'Engagement'}
          </h1>
          <p className="mt-0.5 text-sm text-slate-600">
            {portalSimple
              ? 'Beoordeel voorgestelde kandidaten, volg gesprekken en plaatsingen.'
              : 'Overzicht voor je organisatie. Klik op een tegel hieronder om naar het blok te gaan.'}
          </p>
        </div>
        {!portalSimple && onToggleReviewMode ? (
          <button
            type="button"
            onClick={onToggleReviewMode}
            className={`self-start rounded-lg border px-3 py-2 text-sm font-semibold transition sm:self-auto ${
              reviewMode
                ? 'border-indigo-600 bg-indigo-50 text-indigo-900'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {reviewMode ? 'Terug' : 'Snel doorlopen'}
          </button>
        ) : null}
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-[200px] flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Zoek op kandidaat of vacature…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-900 shadow-sm outline-none ring-indigo-500/0 transition focus:border-indigo-300 focus:ring-2"
            aria-label="Zoeken"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <SlidersHorizontal className="h-4 w-4 text-slate-400" aria-hidden />
            <label className="sr-only" htmlFor="cec-vacancy-filter">
              Vacature
            </label>
            <select
              id="cec-vacancy-filter"
              value={vacancyFilter}
              onChange={(e) => onVacancyFilterChange(e.target.value)}
              className="max-w-[200px] border-0 bg-transparent text-sm font-semibold text-slate-800 outline-none"
            >
              <option value="all">Alle vacatures</option>
              {vacancies.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.title}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <label className="sr-only" htmlFor="cec-status-filter">
              Status
            </label>
            <select
              id="cec-status-filter"
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="border-0 bg-transparent text-sm font-semibold text-slate-800 outline-none"
            >
              <option value="all">Alle statussen</option>
              <option value="waiting_feedback">Wacht op feedback</option>
              <option value="shortlisted">Shortlist</option>
              <option value="more_info_requested">Meer info</option>
              <option value="interview_requested">Interview</option>
              <option value="on_hold">On hold</option>
              <option value="rejected">Afgewezen</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  </header>
);

export default DashboardHeader;
